import { useSyncExternalStore } from 'react';

const STORAGE_PREFIX = 'racemap-recent-searches';
const MAX_RECENT_SEARCHES = 3;
const NO_SEARCHES: Array<string> = [];

/**
 * Prepends `query` to `queries`, deduping case-insensitively and capping the length.
 * Exported standalone so the list logic is unit-testable without touching localStorage.
 */
export function addRecentSearch(queries: Array<string>, query: string): Array<string> {
  const trimmed = query.trim();
  if (trimmed === '') return queries;

  const withoutDuplicate = queries.filter((q) => q.toLowerCase() !== trimmed.toLowerCase());
  return [trimmed, ...withoutDuplicate].slice(0, MAX_RECENT_SEARCHES);
}

// Keyed by user, so signing in as somebody else on a shared browser does not surface the previous
// account's queries.
const storageKey = (userId: string) => `${STORAGE_PREFIX}:${userId}`;

function readQueries(userId: string): Array<string> {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const parsed = raw != null ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) throw new Error('Expected an array');
    if (!parsed.every((q) => typeof q === 'string' && q.trim() !== ''))
      throw new Error('Expected an array of non-empty strings');

    return parsed.slice(0, MAX_RECENT_SEARCHES);
  } catch {
    return NO_SEARCHES;
  }
}

// Module-level cache + pub/sub, so the header field and the mobile sheet share one list.
// Same shape as useRecentColors, but one list per user.
const cache = new Map<string, Array<string>>();
const listeners = new Set<() => void>();

function getQueries(userId: string | null): Array<string> {
  if (userId == null) return NO_SEARCHES;

  const cached = cache.get(userId);
  if (cached != null) return cached;

  const stored = readQueries(userId);
  cache.set(userId, stored);
  return stored;
}

function setQueries(userId: string, queries: Array<string>) {
  cache.set(userId, queries);
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(queries));
  } catch {
    // ponytail: localStorage can throw (private mode, quota) — the in-memory list still works for this session.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Browser-local list of the queries that led to opening a result, for one user. */
export function useRecentSearches(userId: string | null): {
  recentSearches: Array<string>;
  rememberSearch: (query: string) => void;
} {
  const recentSearches = useSyncExternalStore(subscribe, () => getQueries(userId));

  return {
    recentSearches,
    rememberSearch: (query: string) => {
      if (userId == null) return;
      setQueries(userId, addRecentSearch(getQueries(userId), query));
    },
  };
}
